/**
 * The taxpayer block a purchase update should carry, derived from the buyer's current form values.
 *
 * This lives in one place because the purchase has more than one save path and they do not share a
 * writer. `updatePurchaseFields` is the obvious one; `createPassengerSaveOnSubmit` is the other, and
 * it builds its purchaser fields by hand and dispatches its own request, so anything expressed only
 * inside `updatePurchaseFields` is silently absent there. PRO2-128 shipped the invoice that way and
 * a buyer who typed a RUC and then edited a passenger was issued a boleta instead of a factura,
 * with `taxpayer_id: null` on the purchase and nothing on screen to say so.
 *
 * A second copy of this mapping would drift, so both callers spread this instead.
 *
 * Three outcomes, and the difference between the last two is the whole point:
 * - an explicit id fills the block (the address defaults to `''` so the request keeps the shape it
 *   has always sent);
 * - an explicit withdrawal sends empty strings, which `purchase_update_params`' `meta.merge!` writes
 *   over the stored values and `createReservePayloadRequest` maps back to absent, so the provider
 *   issues a boleta. `redux-form` deletes a field cleared to `''` when it has no initial value, so
 *   the withdrawal cannot be inferred from the taxpayer keys being gone — it has to be stated;
 * - anything else sends nothing, leaving every caller that does not use the invoice toggle with the
 *   exact payload it had before.
 *
 * It stands down for the billing-company flow, which owns `taxpayerLegalName` and `taxpayerAddress`
 * for a different purpose and must not have them cleared by an invoice toggle. Note that a dirty
 * check keyed on this must stand down with it, or it can never converge — see
 * `docs/solutions/conventions/a-list-with-two-consumers-is-a-shared-function.md`.
 *
 * That stand-down depends on the caller passing `billingCompanyValidationEnabled`, and only the
 * standard checkout does: it is in `CheckoutContainer`'s `keys`. Quick Purchase's callers do not,
 * and do not need to — `PurchaserFields` gates the whole billing-company flow on `!isQuickCheckout`,
 * so the flag is structurally false there rather than accidentally missing. A caller that ever
 * collects both on one surface has to supply it.
 *
 * @param {object} [fields] - Purchaser form values (`invoice`, `taxpayerId`, `taxpayerLegalName`,
 *   `taxpayerAddress`, `billingCompanyValidationEnabled`).
 * @returns {object} The taxpayer keys to merge into the purchase payload, possibly empty.
 */
export default function taxpayerPayloadFields(fields = {}) {
  if (fields.taxpayerId) {
    return {
      taxpayerId: fields.taxpayerId,
      taxpayerLegalName: fields.taxpayerLegalName,
      taxpayerAddress: fields.taxpayerAddress || '',
    };
  }

  if (fields.invoice === false && !fields.billingCompanyValidationEnabled) {
    return { taxpayerId: '', taxpayerLegalName: '', taxpayerAddress: '' };
  }

  return {};
}
